import { ClaimsType } from 'api-validations/auth-claims'
import { organizationDetailsApi, PlanType, RoleType } from 'api-validations/user-api'

import { usePanoramaQuery } from '../api-provider'
import { useOrgCodeOrUndefined } from './ActiveOrgCodeProvider'
import { useAccount } from './useAccount'

export const useOrgInfo = () => useOrgInfoFor(useOrgCodeOrUndefined())

export const useOrgInfoFor = (orgCode: string | undefined) => useOrgInfoLookup(orgCode).orgInfo

export type OrgInfo = NonNullable<ReturnType<typeof useOrgInfoFor>>

export const useOrgInfoLookup = (orgCode: string | undefined) => {
  const account = useAccount()

  const orgsSupportedAsDealer = usePanoramaQuery(
    organizationDetailsApi,
    { orgCode: orgCode ?? '' },
    {
      enabled: orgCode !== undefined,
      select: (data) =>
        data.items.map((o) => ({
          ...o,
          userRoleType: ClaimsType.Dealer, // TODO: this is a hack to assume you're a dealer if we don't find you in the orgMemberships or supporterMemberships, but we should eventually get this info more robustly
          orgName: o.name
        }))
    }
  )

  // Resolve against the full membership set, not the filtered display list: a just-created,
  // unpaid operation (e.g. on the subscribe-with-code page) is a transitory org excluded
  // from `orgMemberships`, but the user still owns it and needs its info here.
  const orgInfo =
    account.data?.allOrgMemberships.find((m) => m.orgCode === orgCode) ??
    account.data?.supporterMemberships.find((m) => m.orgCode === orgCode) ??
    orgsSupportedAsDealer.data?.find((o) => o.orgCode === orgCode)

  return { orgInfo, isPending: account.isPending || orgsSupportedAsDealer.isPending }
}

type RoleCheckable = { userRoleType: RoleType | ClaimsType }

export const OrgLevelPermissions = {
  isOwner: (o: RoleCheckable) => o.userRoleType === RoleType.Owner,
  canManage: (o: RoleCheckable) =>
    o.userRoleType === RoleType.Manager ||
    o.userRoleType === RoleType.Owner ||
    o.userRoleType === ClaimsType.Dealer,
  isSupporter: (o: RoleCheckable) => o.userRoleType === RoleType.Supporter,
  /** The user is a dealer viewing a customer operation (not a member of the org) */
  isDealer: (o: RoleCheckable) => o.userRoleType === ClaimsType.Dealer,
  canContribute: (o: RoleCheckable) =>
    o.userRoleType === RoleType.Manager ||
    o.userRoleType === RoleType.Owner ||
    o.userRoleType === RoleType.Member ||
    o.userRoleType === ClaimsType.Dealer,
  hasNonSupporterRole: (o: RoleCheckable) => o.userRoleType !== RoleType.Supporter
}

/**
 * Returns whether the active organization is a dealership (dealer plan).
 * Returns `false` when there is no active organization or its info hasn't loaded yet.
 */
export const useIsDealerOrg = (): boolean => {
  const orgInfo = useOrgInfo()
  return orgInfo?.planType === PlanType.Dealer
}

/**
 * Returns whether the active organization has opted in to Aurora's LLM data sharing.
 * Returns `false` until the org info has loaded.
 */
export const useIsAuroraEnabled = (): boolean => {
  const orgInfo = useOrgInfo()
  return orgInfo?.llmDataSharingDisabled === false
}
